<template>
  <table class="table-condensed">
    <thead>
      <tr>
        <th class="prev available" @click="prevMonthClick" tabindex="0">
          <span />
        </th>
        <th
          v-if="showDropdowns"
          :colspan="showWeekNumbers ? 6 : 5"
          class="month"
        >
          <div class="row mx-1">
            <div class="month-dropdown-wrapper col">
              <button
                type="button"
                class="month-dropdown-btn"
                @click.stop="toggleMonthDropdown"
              >
                {{ monthName }} ▾
              </button>
              <ul v-if="monthDropdownOpen" class="month-dropdown-list">
                <li
                  v-for="(m, idx) in months"
                  :key="idx"
                  :class="{
                    selected: m.value + 1 === month,
                    disabled: !m.enabled,
                  }"
                  @click.stop="!m.enabled || selectMonth(m.value + 1)"
                >
                  {{ m.label }}
                </li>
              </ul>
            </div>
            <div class="year-dropdown-wrapper col">
              <button
                type="button"
                class="year-dropdown-btn"
                @click.stop="toggleYearDropdown"
              >
                {{ year }} ▾
              </button>
              <ul v-if="yearDropdownOpen" class="year-dropdown-list">
                <li
                  v-for="y in yearOptions"
                  :key="y"
                  :class="{ selected: y === year }"
                  @click.stop="selectYear(y)"
                >
                  {{ y }}
                </li>
              </ul>
            </div>
          </div>
        </th>
        <th v-else :colspan="showWeekNumbers ? 6 : 5" class="month">
          {{ monthName }} {{ year }}
        </th>
        <th class="next available" @click="nextMonthClick" tabindex="0">
          <span />
        </th>
      </tr>
    </thead>
    <tbody>
      <tr>
        <th v-if="showWeekNumbers" class="week">{{ locale.weekLabel }}</th>
        <th v-for="(weekDay, idx) in locale.daysOfWeek" :key="idx">
          {{ weekDay }}
        </th>
      </tr>
      <tr v-for="(dateRow, index) in calendar" :key="index">
        <td v-if="showWeekNumbers && (index % 7 || index === 0)" class="week">
          {{ $dateUtil.weekNumber(dateRow[0]) }}
        </td>
        <td
          v-for="(date, idx) in dateRow"
          :class="dayClass(date)"
          @click="$emit('date-click', date)"
          @mouseover="$emit('hover-date', date)"
          :key="idx"
          :data-date="date.toISOString().substring(0, 10)"
        >
          <slot name="date-slot" :date="date">
            {{ date.getDate() }}
          </slot>
        </td>
      </tr>
    </tbody>
  </table>
</template>

<script>
/* eslint-disable vue/multi-word-component-names */
import dateUtilMixin from "./dateUtilMixin";

export default {
  mixins: [dateUtilMixin],
  name: "calendar",
  props: {
    monthDate: Date,
    localeData: Object,
    start: Date,
    end: Date,
    minDate: Date,
    maxDate: Date,
    showDropdowns: {
      type: Boolean,
      default: false,
    },
    showWeekNumbers: {
      type: Boolean,
      default: false,
    },
    dateFormat: {
      type: Function,
      default: null,
    },
  },
  data() {
    let currentMonthDate = this.monthDate || this.start || new Date();
    return {
      currentMonthDate,
      year_text: currentMonthDate.getFullYear(),
      yearDropdownOpen: false,
      monthDropdownOpen: false,
    };
  },
  mounted() {
    this._outsideClickHandler = (e) => {
      const yearWrapper = this.$el.querySelector(".year-dropdown-wrapper");
      const monthWrapper = this.$el.querySelector(".month-dropdown-wrapper");
      if (yearWrapper && !yearWrapper.contains(e.target)) {
        this.yearDropdownOpen = false;
      }
      if (monthWrapper && !monthWrapper.contains(e.target)) {
        this.monthDropdownOpen = false;
      }
    };
    document.addEventListener("click", this._outsideClickHandler);
  },
  beforeDestroy() {
    document.removeEventListener("click", this._outsideClickHandler);
  },
  methods: {
    prevMonthClick() {
      this.changeMonthDate(this.$dateUtil.prevMonth(this.currentMonthDate));
    },
    nextMonthClick() {
      this.changeMonthDate(this.$dateUtil.nextMonth(this.currentMonthDate));
    },
    changeMonthDate(date, emit = true) {
      let year_month = this.$dateUtil.yearMonth(this.currentMonthDate);
      this.currentMonthDate = this.$dateUtil.validateDateRange(
        date,
        this.minDate,
        this.maxDate
      );
      // console.info(date, this.currentMonthDate)
      if (
        emit &&
        year_month !== this.$dateUtil.yearMonth(this.currentMonthDate)
      ) {
        this.$emit("change-month", {
          month: this.currentMonthDate.getMonth() + 1,
          year: this.currentMonthDate.getFullYear(),
        });
      }
      this.checkYear();
    },
    dayClass(date) {
      let dt = new Date(date);
      dt.setHours(0, 0, 0, 0);
      let start = new Date(this.start);
      start.setHours(0, 0, 0, 0);
      let end = new Date(this.end);
      end.setHours(0, 0, 0, 0);

      let dt_min_compare = new Date(dt);
      dt_min_compare.setHours(23, 59, 59, 999);

      let classes = {
        off: date.getMonth() + 1 !== this.month,
        weekend: date.getDay() === 6 || date.getDay() === 0,
        today: dt.setHours(0, 0, 0, 0) == new Date().setHours(0, 0, 0, 0),
        active:
          dt.setHours(0, 0, 0, 0) ==
            new Date(this.start).setHours(0, 0, 0, 0) ||
          dt.setHours(0, 0, 0, 0) == new Date(this.end).setHours(0, 0, 0, 0),
        "in-range": dt >= start && dt <= end,
        "start-date": dt.getTime() === start.getTime(),
        "end-date": dt.getTime() === end.getTime(),
        disabled:
          (this.minDate && dt_min_compare.getTime() < this.minDate.getTime()) ||
          (this.maxDate && dt.getTime() > this.maxDate.getTime()),
      };
      return this.dateFormat ? this.dateFormat(classes, date) : classes;
    },
    checkYear() {
      this.$nextTick(() => {
        this.year_text = this.monthDate.getFullYear();
      });
    },
    selectYear(y) {
      this.year = y;
      this.yearDropdownOpen = false;
    },
    selectMonth(m) {
      this.month = m;
      this.monthDropdownOpen = false;
    },
    toggleMonthDropdown() {
      this.monthDropdownOpen = !this.monthDropdownOpen;
      this.yearDropdownOpen = false;
      if (this.monthDropdownOpen) {
        this.$nextTick(() => {
          const list = this.$el.querySelector(".month-dropdown-list");
          const selected = this.$el.querySelector(
            ".month-dropdown-list .selected"
          );
          if (list && selected) {
            list.scrollTop = selected.offsetTop - list.clientHeight / 2;
          }
        });
      }
    },
    toggleYearDropdown() {
      this.yearDropdownOpen = !this.yearDropdownOpen;
      this.monthDropdownOpen = false;
      if (this.yearDropdownOpen) {
        this.$nextTick(() => {
          const list = this.$el.querySelector(".year-dropdown-list");
          const selected = this.$el.querySelector(
            ".year-dropdown-list .selected"
          );
          if (list && selected) {
            list.scrollTop = selected.offsetTop - list.clientHeight / 2;
          }
        });
      }
    },
  },
  computed: {
    monthName() {
      return this.locale.monthNames[this.currentMonthDate.getMonth()];
    },
    year: {
      get() {
        //return this.currentMonthDate.getFullYear()
        return this.year_text;
      },
      set(value) {
        this.year_text = value;
        let newDate = this.$dateUtil.validateDateRange(
          new Date(value, this.month, 1),
          this.minDate,
          this.maxDate
        );
        if (this.$dateUtil.isValidDate(newDate)) {
          this.$emit("change-month", {
            month: newDate.getMonth(),
            year: newDate.getFullYear(),
          });
        }
      },
    },
    month: {
      get() {
        return this.currentMonthDate.getMonth() + 1;
      },
      set(value) {
        let newDate = this.$dateUtil.validateDateRange(
          new Date(this.year, value - 1, 1),
          this.minDate,
          this.maxDate
        );

        this.$emit("change-month", {
          month: newDate.getMonth() + 1,
          year: newDate.getFullYear(),
        });
      },
    },
    calendar() {
      let month = this.month;
      let year = this.currentMonthDate.getFullYear();
      let firstDay = new Date(year, month - 1, 1);
      let lastMonth = this.$dateUtil.prevMonth(firstDay).getMonth() + 1;
      let lastYear = this.$dateUtil.prevMonth(firstDay).getFullYear();
      let daysInLastMonth = new Date(lastYear, month - 1, 0).getDate();

      let dayOfWeek = firstDay.getDay();

      let calendar = [];

      for (let i = 0; i < 6; i++) {
        calendar[i] = [];
      }

      let startDay = daysInLastMonth - dayOfWeek + this.locale.firstDay + 1;
      if (startDay > daysInLastMonth) startDay -= 7;

      if (dayOfWeek === this.locale.firstDay) startDay = daysInLastMonth - 6;

      let curDate = new Date(lastYear, lastMonth - 1, startDay, 12, 0, 0);
      for (
        let i = 0, col = 0, row = 0;
        i < 6 * 7;
        i++, col++, curDate.setDate(curDate.getDate() + 1)
      ) {
        if (i > 0 && col % 7 === 0) {
          col = 0;
          row++;
        }
        calendar[row][col] = new Date(curDate.getTime());
      }

      return calendar;
    },
    yearOptions() {
      const currentYear = new Date().getFullYear();
      const years = [];
      for (let y = 1950; y <= currentYear + 5; y++) {
        years.push(y);
      }
      return years;
    },
    months() {
      return this.locale.monthNames.map((m, idx) => ({
        label: m,
        value: idx,
        enabled:
          (!this.maxDate || this.maxDate >= new Date(this.year, idx, 1)) &&
          (!this.minDate || this.minDate <= new Date(this.year, idx + 1, 0)),
      }));
    },
    locale() {
      return this.$dateUtil.localeData(this.localeData);
    },
  },
  watch: {
    monthDate(value) {
      if (this.currentMonthDate.getTime() !== value.getTime()) {
        this.changeMonthDate(value, false);
      }
    },
  },
};
</script>

<style scoped lang="scss">
th,
td {
  padding: 2px;
  background-color: white;
}

td.today {
  font-weight: bold;
}

td.disabled {
  pointer-events: none;
  background-color: #eee;
  border-radius: 0;
  opacity: 0.6;
}

td.in-range {
  background-color: rgba(0, 180, 120, 0.12) !important;
  border-color: transparent !important;
  color: #000 !important;
  border-radius: 0 !important;
}

td.active,
td.active:hover,
td.start-date,
td.end-date,
td.in-range.active,
td.in-range.start-date,
td.in-range.end-date {
  background-color: #00b478 !important;
  border-color: transparent !important;
  color: #fff !important;
}

td:hover {
  background-color: rgba(0, 180, 120, 0.2) !important;
  border-color: transparent !important;
}

td.active:hover,
td.start-date:hover,
td.end-date:hover {
  background-color: #00b478 !important;
  color: #fff !important;
}

td.in-range:hover {
  background-color: rgba(0, 180, 120, 0.25) !important;
}

td.in-range.active:hover,
td.in-range.start-date:hover,
td.in-range.end-date:hover,
td.active:hover,
td.start-date:hover,
td.end-date:hover {
  background-color: #00b478 !important;
  color: #fff !important;
}

td.disabled:hover {
  background-color: #eee !important;
}

td.off,
td.off.in-range,
td.off.active,
td.off.start-date,
td.off.end-date,
td.off:hover,
td.off.in-range:hover,
td.off.active:hover,
td.off.start-date:hover,
td.off.end-date:hover {
  background-color: white !important;
  border-color: transparent !important;
  color: #999 !important;
}

tbody tr:first-child th {
  cursor: default !important;
  pointer-events: none;
}

@function str-replace($string, $search, $replace: "") {
  $index: str-index($string, $search);

  @if $index {
    @return str-slice($string, 1, $index - 1) + $replace +
      str-replace(
        str-slice($string, $index + str-length($search)),
        $search,
        $replace
      );
  }

  @return $string;
}

$carousel-control-color: #ccc !default;
$viewbox: "-2 -2 10 10";
$carousel-control-prev-icon-bg: str-replace(
  url("data:image/svg+xml;charset=utf8,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='#{$viewbox}'%3E%3Cpath d='M5.25 0l-4 4 4 4 1.5-1.5-2.5-2.5 2.5-2.5-1.5-1.5z'/%3E%3C/svg%3E"),
  "#",
  "%23"
) !default;
$carousel-control-next-icon-bg: str-replace(
  url("data:image/svg+xml;charset=utf8,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='#{$viewbox}'%3E%3Cpath d='M2.75 0l-1.5 1.5 2.5 2.5-2.5 2.5 1.5 1.5 4-4-4-4z'/%3E%3C/svg%3E"),
  "#",
  "%23"
) !default;

.fa {
  display: inline-block;
  width: 100%;
  height: 100%;
  background: transparent no-repeat center center;
  background-size: 100% 100%;
  fill: $carousel-control-color;
}

.prev,
.next {
  &:hover {
    background-color: transparent !important;
  }

  .fa:hover {
    opacity: 0.6;
  }
}

.chevron-left {
  width: 16px;
  height: 16px;
  display: block;
  background-image: $carousel-control-prev-icon-bg;
}

.chevron-right {
  width: 16px;
  height: 16px;
  display: block;
  background-image: $carousel-control-next-icon-bg;
}

.yearselect {
  padding-right: 1px;
  border: none;
  appearance: menulist;
}

.month-dropdown-wrapper {
  position: relative;
  display: inline-block;
  width: 80px;
}

.month-dropdown-btn {
  background: none;
  border: none;
  font-size: 13px;
  cursor: pointer;
  padding: 2px 0;
  white-space: nowrap;
  width: 100%;
  text-align: center;
}

.month-dropdown-list {
  position: absolute;
  top: 100%;
  left: 0;
  z-index: 9999;
  background: #fff;
  border: 1px solid #ccc;
  border-radius: 4px;
  list-style: none;
  margin: 2px 0 0;
  padding: 0;
  width: 100%;
  max-height: 180px;
  overflow-y: auto;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);

  li {
    padding: 5px 0;
    cursor: pointer;
    font-size: 13px;
    text-align: center;
    font-weight: normal;

    &:hover {
      background: #f0f0f0;
    }

    &.selected {
      background: #00b478;
      color: #fff;
    }

    &.disabled {
      color: #aaa;
      cursor: not-allowed;
      &:hover {
        background: none;
      }
    }
  }
}

.year-dropdown-wrapper {
  position: relative;
  display: inline-block;
  width: 58px;
}

.year-dropdown-btn {
  background: none;
  border: none;
  font-size: 13px;
  cursor: pointer;
  padding: 2px 0;
  white-space: nowrap;
  width: 100%;
  text-align: center;
}

.year-dropdown-list {
  position: absolute;
  top: 100%;
  left: 0;
  z-index: 9999;
  background: #fff;
  border: 1px solid #ccc;
  border-radius: 4px;
  list-style: none;
  margin: 2px 0 0;
  padding: 0;
  width: 100%;
  max-height: 180px;
  overflow-y: auto;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);

  li {
    padding: 5px 0;
    cursor: pointer;
    font-size: 13px;
    text-align: center;
    font-weight: normal;

    &:hover {
      background: #f0f0f0;
    }

    &.selected {
      background: #00b478;
      color: #fff;
    }
  }
}

.monthselect {
  border: none;
}
</style>
